import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useRef, useState } from 'react';
import type { Messages } from '@repere/core';
import { Alerte, Bouton, Invisible, Picto, Vide } from '../ui';
import { messageDErreur, useSession } from '../auth/session';
import { cleDuJour, dateComplete, heure } from './dates';
import { useTraduction } from '../i18n/langue';
import type { ParentDuDossier } from './types';

type Visibilite = 'shared' | 'private';

interface EntreeJournal {
  id: string;
  caseFileId: string;
  sequence: string;
  body: string;
  visibility: Visibilite;
  createdAt: string;
  authorId: string | null;
  hash: string;
}

type Filtre = 'tout' | Visibilite;

/**
 * Libellés des deux fils.
 *
 * Ils disent qui verra l'entrée, jamais ce qu'elle vaut : rien n'oppose « officiel » à
 * « officieux », rien ne suggère qu'une note privée serait une pièce à charge en réserve.
 */
function visibilites(
  m: Messages['dossier']['journal'],
): { valeur: Visibilite; libelle: string; aide: string }[] {
  return [
    { valeur: 'shared', libelle: m.visibiliteAutre, aide: m.visibiliteAutreAide },
    { valeur: 'private', libelle: m.visibiliteMoi, aide: m.visibiliteMoiAide },
  ];
}

/**
 * Filtres de lecture — distincts du choix d'écriture ci-dessus.
 *
 * « Qu'est-ce que l'autre parent voit ? » est la question qu'on se pose en relisant un
 * journal, et rien n'y répondait : il fallait parcourir la liste en lisant les pastilles.
 */
function filtres(m: Messages['dossier']['journal']): { valeur: Filtre; libelle: string }[] {
  return [
    { valeur: 'tout', libelle: m.filtreTout },
    { valeur: 'shared', libelle: m.filtrePartagees },
    { valeur: 'private', libelle: m.filtrePrivees },
  ];
}

/**
 * Journal du dossier — la « main courante » (docs/02 §7).
 *
 * Un registre, pas une conversation : on y consigne des faits, personne n'y répond, et
 * aucune entrée n'attend l'accord de qui que ce soit. C'est aussi le module qui rend le
 * mode « parent seul » utile à lui seul, d'où sa présence même sans second parent.
 *
 * Le choix du fil est demandé à chaque entrée, sans valeur pré-cochée : partager ou non
 * un fait est une décision, et une case cochée d'avance la prendrait à la place du parent.
 */
export function Journal({
  caseFileId,
  parents,
  monUserId,
  modeSolo,
}: {
  caseFileId: string;
  parents: ParentDuDossier[];
  monUserId: string | null;
  modeSolo: boolean;
}) {
  const { client } = useSession();
  const { locale, messages } = useTraduction();
  const m = messages.dossier.journal;
  const queryClient = useQueryClient();
  const [texte, setTexte] = useState('');
  const [visibilite, setVisibilite] = useState<Visibilite | ''>('');
  const [filtre, setFiltre] = useState<Filtre>('tout');

  /**
   * L'immuabilité tenait un bandeau ambré pleine largeur, entre les filtres et le
   * registre — sur téléphone, l'essentiel de ce qu'on venait lire pour compter deux
   * écrans de défilement. Même geste que la Messagerie : une bille d'information, qu'on
   * n'ouvre qu'une fois qu'on a compris ce que le journal fait.
   */
  const [infoImmuableOuverte, setInfoImmuableOuverte] = useState(false);
  const infoImmuableBloc = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!infoImmuableOuverte) return;
    const auClic = (evenement: MouseEvent) => {
      if (!infoImmuableBloc.current?.contains(evenement.target as Node)) {
        setInfoImmuableOuverte(false);
      }
    };
    const auClavier = (evenement: KeyboardEvent) => {
      if (evenement.key === 'Escape') setInfoImmuableOuverte(false);
    };
    document.addEventListener('mousedown', auClic);
    document.addEventListener('keydown', auClavier);
    return () => {
      document.removeEventListener('mousedown', auClic);
      document.removeEventListener('keydown', auClavier);
    };
  }, [infoImmuableOuverte]);

  const entrees = useQuery({
    queryKey: ['journal', caseFileId],
    queryFn: () => client.request<EntreeJournal[]>(`/case-files/${caseFileId}/journal`),
  });

  const consigner = useMutation({
    mutationFn: () =>
      client.request(`/case-files/${caseFileId}/journal`, {
        method: 'POST',
        body: { body: texte, visibility: visibilite },
      }),
    onSuccess: () => {
      setTexte('');
      setVisibilite('');
      setProposition(null);
      void queryClient.invalidateQueries({ queryKey: ['journal', caseFileId] });
    },
  });

  /**
   * La reformulation proposée, ou `null` — l'état par défaut, de loin le plus fréquent.
   *
   * Même dispositif que la Messagerie, et pour la même raison : elle ne vit **que** dans
   * l'écran, jamais en base ni dans le cache de requêtes. Rien de ce parcours ne survit à
   * la fermeture de l'onglet — pas même le fait qu'il a eu lieu, ce qui vaut aussi pour
   * une entrée partagée que l'autre parent pourra lire (T-021, docs/02 §4 bis).
   */
  const [proposition, setProposition] = useState<string | null>(null);

  const reformuler = useMutation({
    mutationFn: () =>
      client.request<{ proposition: string | null; motif?: string }>(
        `/case-files/${caseFileId}/journal/reformulate`,
        { method: 'POST', body: { body: texte } },
      ),
    onSuccess: (resultat) => setProposition(resultat.proposition),
  });

  const nomDe = (authorId: string | null): string => {
    if (authorId === monUserId) return m.auteurVous;
    return parents.find((p) => !p.isViewer)?.displayName ?? m.auteurAutreParent;
  };

  const toutes = entrees.data ?? [];
  const visibles = filtre === 'tout' ? toutes : toutes.filter((e) => e.visibility === filtre);
  const compteDu = (valeur: Filtre): number =>
    valeur === 'tout' ? toutes.length : toutes.filter((e) => e.visibility === valeur).length;

  const VIDES: Record<Filtre, string> = {
    tout: m.videTout,
    shared: m.videPartagees,
    private: m.videPrivees,
  };

  const optionsVisibilite = visibilites(m);
  const optionsFiltres = filtres(m);

  const aideDuChoix = visibilite
    ? optionsVisibilite.find((v) => v.valeur === visibilite)?.aide
    : m.aideChoixDefaut;

  return (
    <>
      {modeSolo ? <p className="discret">{m.modeSoloTexte}</p> : null}

      <div className="filtres" role="group" aria-label={m.ariaFiltrer}>
        {optionsFiltres.map((option) => {
          const actif = option.valeur === filtre;
          const compte = compteDu(option.valeur);
          return (
            <button
              key={option.valeur}
              type="button"
              className={actif ? 'filtre-onglet actif' : 'filtre-onglet'}
              aria-pressed={actif}
              onClick={() => setFiltre(option.valeur)}
            >
              {option.libelle}
              {compte > 0 ? (
                <>
                  <span className="filtre-compte" aria-hidden="true">
                    {compte > 99 ? '99+' : compte}
                  </span>
                  <Invisible>{m.entreeSuffixe(compte)}</Invisible>
                </>
              ) : null}
            </button>
          );
        })}
      </div>

      {entrees.isError ? (
        <Alerte titre={m.erreurChargement}>{messageDErreur(entrees.error)}</Alerte>
      ) : null}

      {/* Le registre a son propre cadre, comme la conversation de la Messagerie : c'est ce
          qui donne à la bille d'information un coin où se poser plutôt qu'une rangée
          entière pour un geste qu'on ne fait qu'une fois. */}
      <div className="registre-cadre">
        <div className="info-bulle" ref={infoImmuableBloc}>
          <button
            type="button"
            className="info-bulle-bouton"
            aria-expanded={infoImmuableOuverte}
            aria-controls="mention-immuable-journal-texte"
            onClick={() => setInfoImmuableOuverte((etat) => !etat)}
          >
            <span aria-hidden="true">i</span>
            <Invisible>{m.mentionImmuableBouton}</Invisible>
          </button>
          {infoImmuableOuverte ? (
            <p id="mention-immuable-journal-texte" className="mention-immuable info-bulle-texte">
              {m.mentionImmuable}
            </p>
          ) : null}
        </div>

        <ol className="registre">
          {visibles.map((entree, index) => {
            const quand = new Date(entree.createdAt);
            const prive = entree.visibility === 'private';
            const precedente = visibles[index - 1];
            const nouveauJour =
              !precedente || cleDuJour(new Date(precedente.createdAt)) !== cleDuJour(quand);

            return (
              <Fragment key={entree.id}>
                {nouveauJour ? (
                  <li className="registre-jour" aria-hidden="true">
                    <span>{dateComplete(quand, locale)}</span>
                  </li>
                ) : null}
                <li className={prive ? 'registre-entree privee' : 'registre-entree'}>
                  <p className="entete">
                    {/* L'heure et l'auteur restent deux éléments : la date complète est
                      déjà portée par le séparateur de jour, et l'auteur doit pouvoir se
                      repérer seul — à l'œil comme au test. */}
                    <span>
                      {heure(quand, locale)} · <span>{nomDe(entree.authorId)}</span>
                    </span>
                    <span className={prive ? 'marque-privee' : 'marque-partagee'}>
                      {prive ? m.marquePrivee : m.marquePartagee}
                    </span>
                  </p>
                  <p className="corps">{entree.body}</p>
                  {/* Le séparateur de jour est masqué au lecteur d'écran : la date complète
                    voyage ici, avec chaque entrée. */}
                  <Invisible>
                    {dateComplete(quand, locale)} à {heure(quand, locale)}.{' '}
                    {prive ? m.invisibleSuffixePrivee : m.invisibleSuffixePartagee}
                  </Invisible>
                </li>
              </Fragment>
            );
          })}

          {visibles.length === 0 ? (
            <li className="registre-vide">
              <Vide categorie="journal">{VIDES[filtre]}</Vide>
            </li>
          ) : null}
        </ol>
      </div>

      <form
        className="consigner"
        onSubmit={(e) => {
          e.preventDefault();
          consigner.mutate();
        }}
      >
        {/* Même dessin que la barre de saisie de la Messagerie : le choix rare — qui voit
            l'entrée — sur sa propre rangée, pour que le champ de texte tienne, seul avec
            l'envoi, toute la largeur de l'écran. La reformulation, elle, reste à côté de
            l'envoi : c'est un geste sur ce texte-là, juste avant de le consigner. */}
        <div className="consigner-visibilite-groupe" role="group" aria-label={m.ariaQuiPeutVoir}>
          {optionsVisibilite.map((option) => (
            <button
              key={option.valeur}
              type="button"
              className={
                visibilite === option.valeur ? 'consigner-visibilite actif' : 'consigner-visibilite'
              }
              aria-pressed={visibilite === option.valeur}
              onClick={() => setVisibilite(option.valeur)}
            >
              {option.libelle}
            </button>
          ))}
        </div>

        <p className="aide" id="journal-aide">
          {aideDuChoix}
        </p>

        <div className="redaction-envoi">
          <label htmlFor="journal-corps">
            <Invisible>{m.labelFait}</Invisible>
          </label>
          <textarea
            id="journal-corps"
            rows={2}
            value={texte}
            onChange={(e) => setTexte(e.target.value)}
            maxLength={10_000}
            placeholder={m.placeholderFait}
            aria-describedby="journal-aide"
            required
          />

          <button
            type="button"
            className="redaction-outil"
            onClick={() => reformuler.mutate()}
            disabled={!texte.trim() || reformuler.isPending}
            title={m.reformuler}
          >
            <Picto categorie="journal" />
            <Invisible>{m.reformuler}</Invisible>
          </button>

          <button
            type="submit"
            className="redaction-envoyer"
            disabled={!texte.trim() || !visibilite || consigner.isPending}
            title={consigner.isPending ? m.enregistrementEnCours : m.consigner}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 12.5 L9.5 18 L20 6"
              />
            </svg>
            <Invisible>{consigner.isPending ? m.enregistrementEnCours : m.consigner}</Invisible>
          </button>
        </div>

        {/* La proposition, sous la barre de saisie et jamais à sa place : le brouillon
            reste à l'écran, intact, tant que le parent n'a pas choisi. L'IA propose, elle
            ne remplace pas — et consigner n'a jamais été conditionné à ce détour. */}
        {reformuler.isPending ? (
          <div className="redaction-repli">
            <p className="discret" style={{ margin: 0 }}>
              {m.reformulationEnCours}
            </p>
          </div>
        ) : null}

        {proposition ? (
          <div className="redaction-repli reformulation">
            <p className="reformulation-titre">{m.reformulationTitre}</p>
            <p className="reformulation-texte">{proposition}</p>
            <p>
              <Bouton
                onClick={() => {
                  setTexte(proposition);
                  setProposition(null);
                }}
              >
                {m.reformulationUtiliser}
              </Bouton>{' '}
              <Bouton variante="secondaire" onClick={() => setProposition(null)}>
                {m.reformulationGarder}
              </Bouton>
            </p>
            <p className="discret" style={{ marginBottom: 0 }}>
              {m.reformulationAide}
            </p>
          </div>
        ) : null}

        {reformuler.isSuccess && !proposition && !reformuler.isPending ? (
          <div className="redaction-repli">
            <p className="discret" style={{ margin: 0 }}>
              {reformuler.data?.motif === 'budget'
                ? m.reformulationBudget
                : reformuler.data?.motif === 'trop_long'
                  ? m.reformulationTropLong
                  : reformuler.data?.motif
                    ? m.reformulationIndisponible
                    : m.reformulationDejaNeutre}
            </p>
          </div>
        ) : null}

        {reformuler.isError ? (
          <div className="redaction-repli">
            <p className="discret" style={{ margin: 0 }}>
              {m.reformulationIndisponible}
            </p>
          </div>
        ) : null}

        {consigner.isError ? (
          <Alerte titre={m.erreurEnregistrement}>{messageDErreur(consigner.error)}</Alerte>
        ) : null}
      </form>
    </>
  );
}
